import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FaSpotify, FaApple, FaYoutube, FaAmazon, FaSoundcloud, FaInstagram, FaTwitter, FaFacebook, FaTiktok, FaBandcamp, FaExternalLinkAlt, FaHeadphones, FaMusic } from "react-icons/fa";
import { SiTidal } from "react-icons/si";

const DSP_ICONS = {
  spotify: FaSpotify,
  apple: FaApple,
  youtube: FaYoutube,
  tidal: SiTidal,
  amazon: FaAmazon,
  deezer: FaHeadphones,
  soundcloud: FaSoundcloud,
};

const SOCIAL_ICONS = {
  instagram: FaInstagram,
  twitter: FaTwitter,
  facebook: FaFacebook,
  tiktok: FaTiktok,
  bandcamp: FaBandcamp,
  youtube: FaYoutube,
};

const DSP_COLORS = {
  spotify: "hover:text-white hover:bg-zinc-700",
  apple: "hover:text-white hover:bg-zinc-700",
  youtube: "hover:text-white hover:bg-zinc-700",
  tidal: "hover:text-white hover:bg-zinc-700",
  amazon: "hover:text-white hover:bg-zinc-700",
  deezer: "hover:text-white hover:bg-zinc-700",
  soundcloud: "hover:text-white hover:bg-zinc-700",
};

const SOCIAL_COLORS = {
  instagram: "hover:text-white hover:bg-zinc-700",
  twitter: "hover:text-white hover:bg-zinc-700",
  facebook: "hover:text-white hover:bg-zinc-700",
  tiktok: "hover:text-white hover:bg-zinc-700",
  bandcamp: "hover:text-white hover:bg-zinc-700",
  youtube: "hover:text-white hover:bg-zinc-700",
};

export default function ReleaseCard({ release, dspServices, socialPlatforms }) {
  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getDSPIcon = (serviceId) => {
    const service = dspServices.find((s) => s.id === serviceId);
    if (!service) return FaExternalLinkAlt;
    return DSP_ICONS[service.icon] || FaExternalLinkAlt;
  };

  const getSocialIcon = (platformId) => {
    const platform = socialPlatforms.find((p) => p.id === platformId);
    if (!platform) return FaExternalLinkAlt;
    return SOCIAL_ICONS[platform.icon] || FaExternalLinkAlt;
  };

  const getDSPColor = (serviceId) => {
    const service = dspServices.find((s) => s.id === serviceId);
    if (!service) return "hover:text-accent-primary";
    return DSP_COLORS[service.icon] || "hover:text-accent-primary";
  };

  const getSocialColor = (platformId) => {
    const platform = socialPlatforms.find((p) => p.id === platformId);
    if (!platform) return "hover:text-accent-primary";
    return SOCIAL_COLORS[platform.icon] || "hover:text-accent-primary";
  };

  return (
    <motion.div
      whileHover={{ scale: 1.02 }}
      className="group relative bg-background-surface rounded-xl overflow-hidden border border-zinc-800 hover:border-zinc-700 transition-all duration-300 glow-hover"
      data-testid={`release-card-${release.id}`}
    >
      {/* Artwork */}
      <Link to={`/release/${release.id}`} data-testid={`release-link-${release.id}`}>
        <div className="relative aspect-square overflow-hidden">
          <img
            src={release.artwork_url}
            alt={release.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
            data-testid={`release-artwork-${release.id}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent" />
          
          {/* Date Badge */}
          <div className="absolute top-3 right-3 px-3 py-1 rounded-full bg-black/60 backdrop-blur-sm border border-zinc-700">
            <span className="text-xs font-space text-zinc-300">{formatDate(release.release_date)}</span>
          </div>
        </div>
      </Link>

      {/* Content */}
      <div className="p-4">
        <Link to={`/release/${release.id}`}>
          <h3 className="text-lg font-syne font-bold text-white mb-1 truncate group-hover:text-accent-primary transition-colors" data-testid={`release-title-${release.id}`}>
            {release.title}
          </h3>
          <p className="text-sm text-zinc-400 mb-4" data-testid={`release-artist-${release.id}`}>{release.artist}</p>
        </Link>

        {/* DSP Links */}
        {release.dsp_links && release.dsp_links.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-3" data-testid={`release-dsp-links-${release.id}`}>
            {release.dsp_links.map((link, idx) => {
              const Icon = getDSPIcon(link.service_id);
              const colorClass = getDSPColor(link.service_id);
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 ${colorClass} transition-colors`}
                  data-testid={`dsp-link-${link.service_id}-${release.id}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        )}

        {/* Social Links */}
        {release.social_links && release.social_links.length > 0 && (
          <div className="flex flex-wrap gap-2" data-testid={`release-social-links-${release.id}`}>
            {release.social_links.map((link, idx) => {
              const Icon = getSocialIcon(link.platform_id);
              const colorClass = getSocialColor(link.platform_id);
              return (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-zinc-400 ${colorClass} transition-colors`}
                  data-testid={`social-link-${link.platform_id}-${release.id}`}
                >
                  <Icon className="w-4 h-4" />
                </a>
              );
            })}
          </div>
        )}
      </div>
    </motion.div>
  );
}
